/**
 * (EXPERIMENTAL AS OF 2026-06-08) Simple JSON differ.
 * 
 *
 *   import { diffJson, diffJsonReport } from './jsonDiff';
 *   const diffs = diffJson(objA, objB);          // structured list of differences
 *   const report = diffJsonReport(objA, objB);   // human-readable report, '' if equal
 *
 * Arrays are aligned via longest-common-subsequence, and unmatched elements
 * are paired up by structural similarity, so insertions/removals and small
 * edits inside large elements produce granular diffs rather than cascades of
 * false "changed" entries.
 */

export type JsonValue =
  | string
  | number
  | boolean
  | null
  | JsonValue[]
  | { [key: string]: JsonValue };

export type DiffType = 'added' | 'removed' | 'changed';

export interface Diff {
  type: DiffType;
  /** e.g. "users[0].email" */
  path: string;
  oldValue?: JsonValue;
  newValue?: JsonValue;
  /**
   * Human-readable trail of ancestor labels, e.g. ["Section: Income",
   * "Question: Household size"]. Only present when diffing with a
   * `labelForNode` option and at least one ancestor produced a label.
   */
  breadcrumbs?: string[];
}

export interface DiffJsonOptions {
  /**
   * Optional human-readable label for a node, e.g. "Section: Income".
   * Called for each value the differ descends into (and for removed/added
   * values themselves); returning undefined adds no breadcrumb. Structural
   * paths are unaffected — labels accumulate in `Diff.breadcrumbs`.
   */
  labelForNode?: (node: JsonValue) => string | undefined;
}

function isPlainObject(v: JsonValue): v is { [key: string]: JsonValue } {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function joinPath(parent: string, key: string | number): string {
  if (typeof key === 'number') return `${parent}[${key}]`;
  return parent ? `${parent}.${key}` : key;
}

export function deepEqual(a: JsonValue, b: JsonValue): boolean {
  if (a === b) return true;
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = Object.keys(a);
    if (keys.length !== Object.keys(b).length) return false;
    return keys.every((k) => Object.hasOwn(b, k) && deepEqual(a[k], b[k]));
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((v, i) => deepEqual(v, b[i]));
  }
  return false;
}

/**
 * How alike two unequal values must be before a deletion/insertion pair is
 * treated as an edit of one element (recursive diff) rather than a removal
 * plus an addition. Strictly greater-than, so e.g. two single-key objects
 * whose only key differs (score 0.5) stay a removal + addition.
 */
const SIMILARITY_THRESHOLD = 0.5;

/** Score how alike two values are, from 0 (unrelated) to 1 (deep-equal). */
function similarity(a: JsonValue, b: JsonValue): number {
  if (deepEqual(a, b)) return 1;
  if (isPlainObject(a) && isPlainObject(b)) {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    if (keys.size === 0) return 1;
    let score = 0;
    for (const key of keys) {
      if (Object.hasOwn(a, key) && Object.hasOwn(b, key)) {
        // Half credit for the shared key itself, the rest scaled by how
        // similar the two values are — so a small change deep inside a
        // large value barely dents the score.
        score += 0.5 + 0.5 * similarity(a[key], b[key]);
      }
    }
    return score / keys.size;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    const len = Math.max(a.length, b.length);
    if (len === 0) return 1;
    // Two reads of array likeness: the LCS match ratio catches shifts
    // (inserts/removals leave most elements intact), while positional
    // averaging catches many-small-edits (every element tweaked in place).
    // Take whichever reads more alike.
    const lcsMatches = lcsAlign(a, b).filter((op) => op.op === 'match').length;
    let positional = 0;
    for (let i = 0; i < Math.min(a.length, b.length); i++) {
      positional += similarity(a[i], b[i]);
    }
    return Math.max(lcsMatches, positional) / len;
  }
  // Unequal primitives or mismatched types share nothing. (Same-type
  // primitives are still paired as "changed" — that special case lives in
  // the candidate loop, where it can't inflate recursive object scores.)
  return 0;
}

type ArrayOp =
  | { op: 'match'; i: number; j: number }
  | { op: 'del'; i: number }
  | { op: 'ins'; j: number };

/**
 * Align two arrays via longest-common-subsequence over deep-equal elements,
 * so an insertion/removal shifts the alignment instead of cascading into
 * "changed" entries for every subsequent index.
 */
function lcsAlign(oldArr: JsonValue[], newArr: JsonValue[]): ArrayOp[] {
  const n = oldArr.length;
  const m = newArr.length;
  // dp[i][j] = LCS length of oldArr[i:] vs newArr[j:]
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  const eq: boolean[][] = Array.from({ length: n }, () => new Array<boolean>(m).fill(false));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      eq[i][j] = deepEqual(oldArr[i], newArr[j]);
      dp[i][j] = eq[i][j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }
  const ops: ArrayOp[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (eq[i][j]) {
      ops.push({ op: 'match', i: i++, j: j++ });
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      ops.push({ op: 'del', i: i++ });
    } else {
      ops.push({ op: 'ins', j: j++ });
    }
  }
  while (i < n) ops.push({ op: 'del', i: i++ });
  while (j < m) ops.push({ op: 'ins', j: j++ });
  return ops;
}

/**
 * WARNING: As of 2026-06-08 this is EXPERIMENTAL and shouldn't be relied on for critical use at this time.
 * 
 * Compare two JSON values and return a flat list of differences.
 */
export function diffJson(
  oldValue: JsonValue,
  newValue: JsonValue,
  options: DiffJsonOptions = {}
): Diff[] {
  return diffWalk(oldValue, newValue, options, '', []);
}

/**
 * Extend a breadcrumb trail with the label for a node, preferring the new
 * value's label (current naming) and falling back to the old. Returns the
 * trail unchanged — never mutated — when no label applies.
 */
function extendCrumbs(
  crumbs: string[],
  options: DiffJsonOptions,
  primary: JsonValue,
  fallback?: JsonValue
): string[] {
  if (!options.labelForNode) return crumbs;
  const label =
    options.labelForNode(primary) ??
    (fallback === undefined ? undefined : options.labelForNode(fallback));
  return label === undefined ? crumbs : [...crumbs, label];
}

function withCrumbs(diff: Diff, crumbs: string[]): Diff {
  if (crumbs.length > 0) diff.breadcrumbs = crumbs;
  return diff;
}

function diffWalk(
  oldValue: JsonValue,
  newValue: JsonValue,
  options: DiffJsonOptions,
  path: string,
  crumbs: string[]
): Diff[] {
  // Identical primitives (or identical references)
  if (oldValue === newValue) return [];

  // Both objects: recurse over the union of keys
  if (isPlainObject(oldValue) && isPlainObject(newValue)) {
    const diffs: Diff[] = [];
    const keys = new Set([...Object.keys(oldValue), ...Object.keys(newValue)]);
    for (const key of keys) {
      const childPath = joinPath(path, key);
      if (!Object.hasOwn(newValue, key)) {
        diffs.push(
          withCrumbs(
            { type: 'removed', path: childPath, oldValue: oldValue[key] },
            extendCrumbs(crumbs, options, oldValue[key])
          )
        );
      } else if (!Object.hasOwn(oldValue, key)) {
        diffs.push(
          withCrumbs(
            { type: 'added', path: childPath, newValue: newValue[key] },
            extendCrumbs(crumbs, options, newValue[key])
          )
        );
      } else {
        diffs.push(
          ...diffWalk(
            oldValue[key],
            newValue[key],
            options,
            childPath,
            extendCrumbs(crumbs, options, newValue[key], oldValue[key])
          )
        );
      }
    }
    return diffs;
  }

  // Both arrays: LCS-align elements, then pair up unmatched runs so a
  // modified element still produces a recursive diff rather than a
  // removed+added pair.
  if (Array.isArray(oldValue) && Array.isArray(newValue)) {
    const diffs: Diff[] = [];
    const ops = lcsAlign(oldValue, newValue);
    let k = 0;
    while (k < ops.length) {
      if (ops[k].op === 'match') {
        k++;
        continue;
      }
      // Collect a contiguous run of deletions/insertions between matches.
      const dels: number[] = [];
      const inss: number[] = [];
      while (k < ops.length && ops[k].op !== 'match') {
        const op = ops[k++];
        if (op.op === 'del') dels.push(op.i);
        else if (op.op === 'ins') inss.push(op.j);
      }
      // Pair deletions with insertions most-similar first and recurse into
      // each pair; everything unpaired is a pure removal/addition. Paths for
      // paired and added elements use the NEW array's index; removed use the
      // OLD index.
      const candidates: { d: number; n: number; score: number }[] = [];
      for (let d = 0; d < dels.length; d++) {
        for (let n = 0; n < inss.length; n++) {
          const oldEl = oldValue[dels[d]];
          const newEl = newValue[inss[n]];
          const bothPrimitive =
            (typeof oldEl !== 'object' || oldEl === null) &&
            (typeof newEl !== 'object' || newEl === null);
          // Differing primitives of the same type read better as a single
          // "changed" entry even though they share no content; everything
          // else is scored on actual structural overlap.
          const score = bothPrimitive
            ? oldEl !== null && newEl !== null && typeof oldEl === typeof newEl
              ? 0.6
              : 0
            : similarity(oldEl, newEl);
          if (score > SIMILARITY_THRESHOLD) candidates.push({ d, n, score });
        }
      }
      // Stable sort: equal scores keep positional (d, then n) order.
      candidates.sort((a, b) => b.score - a.score);
      const pairedDel = new Set<number>();
      const pairedIns = new Set<number>();
      for (const { d, n } of candidates) {
        if (pairedDel.has(d) || pairedIns.has(n)) continue;
        pairedDel.add(d);
        pairedIns.add(n);
        diffs.push(
          ...diffWalk(
            oldValue[dels[d]],
            newValue[inss[n]],
            options,
            joinPath(path, inss[n]),
            extendCrumbs(crumbs, options, newValue[inss[n]], oldValue[dels[d]])
          )
        );
      }
      for (let p = 0; p < dels.length; p++) {
        if (pairedDel.has(p)) continue;
        diffs.push(
          withCrumbs(
            { type: 'removed', path: joinPath(path, dels[p]), oldValue: oldValue[dels[p]] },
            extendCrumbs(crumbs, options, oldValue[dels[p]])
          )
        );
      }
      for (let p = 0; p < inss.length; p++) {
        if (pairedIns.has(p)) continue;
        diffs.push(
          withCrumbs(
            { type: 'added', path: joinPath(path, inss[p]), newValue: newValue[inss[p]] },
            extendCrumbs(crumbs, options, newValue[inss[p]])
          )
        );
      }
    }
    return diffs;
  }

  // Mismatched types or differing primitives. Crumbs are NOT extended here:
  // when this value was descended into, the caller already labeled it.
  return [withCrumbs({ type: 'changed', path: path || '(root)', oldValue, newValue }, crumbs)];
}

/** Render diffs in +/- format. */
export function formatDiffs(diffs: Diff[], color = false): string {
  const red = (s: string) => (color ? `\x1b[31m${s}\x1b[0m` : s);
  const green = (s: string) => (color ? `\x1b[32m${s}\x1b[0m` : s);

  const lines: string[] = [];
  for (const diff of diffs) {
    const crumbs = diff.breadcrumbs?.length ? ` — ${diff.breadcrumbs.join(' > ')}` : '';
    lines.push(`${diff.path} (${diff.type})${crumbs}`);
    if (diff.type === 'removed' || diff.type === 'changed') {
      lines.push(red(`- ${JSON.stringify(diff.oldValue)}`));
    }
    if (diff.type === 'added' || diff.type === 'changed') {
      lines.push(green(`+ ${JSON.stringify(diff.newValue)}`));
    }
  }
  return lines.join('\n');
}

export interface DiffJsonReportOptions extends DiffJsonOptions {
  /** Label for the old value, shown as a "--- <label>" header line. */
  oldLabel?: string;
  /** Label for the new value, shown as a "+++ <label>" header line. */
  newLabel?: string;
  /** Wrap removals/additions in ANSI color codes. Default false. */
  color?: boolean;
}

/**
 * WARNING: As of 2026-06-08 this is EXPERIMENTAL and shouldn't be relied on for critical use at this time.
 * 
 * Compare two JSON values and return a complete human-readable report as a
 * single string: optional header lines, the +/- formatted differences, and a
 * summary line. Returns '' when the inputs are equal.
 */
export function diffJsonReport(
  oldValue: JsonValue,
  newValue: JsonValue,
  options: DiffJsonReportOptions = {}
): string {
  const diffs = diffJson(oldValue, newValue, { labelForNode: options.labelForNode });
  if (diffs.length === 0) return '';

  const lines: string[] = [];
  if (options.oldLabel) lines.push(`--- ${options.oldLabel}`);
  if (options.newLabel) lines.push(`+++ ${options.newLabel}`);
  lines.push(formatDiffs(diffs, options.color ?? false));

  const counts: Record<DiffType, number> = { added: 0, removed: 0, changed: 0 };
  for (const d of diffs) counts[d.type]++;
  lines.push(
    '',
    `${diffs.length} differences: ${counts.added} added, ${counts.removed} removed, ${counts.changed} changed`
  );
  return lines.join('\n');
}
